import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';

// project-imports
import Drawer from './Drawer';
import HorizontalBar from './Drawer/HorizontalBar';
import PageChrome from 'layout/Dashboard/PageChrome';
import GlobalSearch from 'components/GlobalSearch';
import {
  GlobalSearchProvider,
  useGlobalSearch,
} from 'contexts/GlobalSearchContext';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';

import { DRAWER_WIDTH, MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import AdminGuard from 'routes/AdminGuard';
import useSubAdminPresence from 'hooks/useSubAdminPresence';
import { V2 } from 'layout/Dashboard/Header/headerV2Tokens';
import 'components/GlobalSearch/globalSearch.css';

const adminMainBg = `
  radial-gradient(ellipse 80% 50% at 0% -10%, ${V2.accentSoft}, transparent 55%),
  radial-gradient(ellipse 60% 40% at 100% 0%, rgba(24, 199, 183, 0.08), transparent 50%),
  linear-gradient(180deg, ${V2.bg} 0%, #f3f5f7 45%, #eef1f4 100%)
`;

// ==============================|| MAIN LAYOUT ||============================== //

function MainLayoutInner() {
  useSubAdminPresence();
  const theme = useTheme();

  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery(theme.breakpoints.down('xl'));
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const { open: searchOpen } = useGlobalSearch();
  const [searchCovered, setSearchCovered] = useState(false);

  const { miniDrawer, menuOrientation } = useConfig();
  const { pathname } = useLocation();
  const mainRef = useRef(null);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (searchOpen) {
      setSearchCovered(true);
      if (mainRef.current) {
        mainRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return undefined;
    }
    const t = setTimeout(() => setSearchCovered(false), 420);
    return () => clearTimeout(t);
  }, [searchOpen]);

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);
  if (menuMasterLoading) return <Loader />;

  return (
    <AdminGuard>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minHeight: '100vh',
          bgcolor: V2.bg,
          fontFamily: V2.fontBody,
          overflow: 'hidden',
        }}
      >
        {!isHorizontal ? <Drawer /> : <HorizontalBar />}

        <Box
          ref={mainRef}
          component="main"
          className={searchCovered ? 'is-search-open' : undefined}
          sx={{
            width: { xs: '100%', lg: `calc(100% - ${DRAWER_WIDTH}px)` },
            flexGrow: 1,
            py: 0,
            px: { xs: 2, sm: 3 },
            background: adminMainBg,
            height: '100vh',
            overflowY: searchCovered ? 'hidden' : 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <PageChrome />
          <Box
            className={`dashboard-content-stack${searchCovered ? ' is-search-open' : ''}`}
            sx={{
              position: 'relative',
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              className={`dashboard-page-outlet${searchCovered ? ' is-search-covered' : ''}`}
            >
              <Container
                maxWidth={false}
                disableGutters
                sx={{
                  width: 'min(1180px, 100%)',
                  mx: 'auto',
                  position: 'relative',
                  minHeight: 'calc(100vh - 110px)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Breadcrumbs />
                <Outlet />
              </Container>
            </div>
            <GlobalSearch />
          </Box>
        </Box>
      </Box>
    </AdminGuard>
  );
}

export default function MainLayout() {
  return (
    <GlobalSearchProvider>
      <MainLayoutInner />
    </GlobalSearchProvider>
  );
}
